import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  // Lock body scroll when modal is open
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [activeModal]);
  return <>
      <footer className="bg-brand-gray text-brand-cream/50 py-8 text-center text-sm border-t border-white/10" data-id="element-50">
        <div className="max-w-4xl mx-auto px-4" data-id="element-51">
          <p className="mb-4" data-id="element-52">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio NO está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>

          <div className="flex justify-center gap-6 mb-4" data-id="element-53">
            <button onClick={() => setActiveModal('privacy')} className="text-brand-cream/60 hover:text-brand-gold underline underline-offset-2 transition-colors cursor-pointer" data-id="element-54">
              Política de Privacidad
            </button>
            <span className="text-brand-cream/30" data-id="element-55">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-brand-cream/60 hover:text-brand-gold underline underline-offset-2 transition-colors cursor-pointer" data-id="element-56">
              Términos y Condiciones
            </button>
          </div>

          <p data-id="element-57">
            © {new Date().getFullYear()} El Escudo Hormonal 40+. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-58">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-59" />}
      </AnimatePresence>
    </>;
}
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-60">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-61" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden z-10" data-id="element-62">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-brand-cream px-6 py-4 flex items-center justify-between z-10" data-id="element-63">
          <h2 className="text-xl font-bold text-brand-green font-heading" data-id="element-64">
            {type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
          </h2>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-brand-cream flex items-center justify-center hover:bg-brand-green hover:text-white transition-colors" aria-label="Cerrar" data-id="element-65">
            <XIcon className="w-5 h-5" data-id="element-66" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)] text-brand-gray/80 text-sm leading-relaxed space-y-5" data-id="element-67">
          {type === 'privacy' ? <PrivacyContent data-id="element-68" /> : <TermsContent data-id="element-69" />}
        </div>
      </motion.div>
    </motion.div>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-brand-gray/50 italic" data-id="element-70">
        Última actualización: Marzo 2026
      </p>

      <div data-id="element-71">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-72">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-73">
          Al realizar una compra en nuestro sitio, recopilamos la información
          necesaria para procesar tu pedido, incluyendo tu nombre, dirección de
          correo electrónico y datos de pago. Los datos de pago son procesados
          de forma segura por nuestro proveedor de pagos y nunca se almacenan en
          nuestros servidores.
        </p>
      </div>

      <div data-id="element-74">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-75">
          2. Uso de la Información
        </h3>
        <p data-id="element-76">Utilizamos tu información personal para:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1" data-id="element-77">
          <li data-id="element-78">Procesar y entregar tu compra digital.</li>
          <li data-id="element-79">Enviarte el acceso a los materiales adquiridos.</li>
          <li data-id="element-80">Comunicarnos contigo sobre tu pedido o soporte técnico.</li>
          <li data-id="element-81">
            Enviarte actualizaciones del producto si has dado tu consentimiento.
          </li>
        </ul>
      </div>

      <div data-id="element-82">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-83">
          3. Protección de Datos
        </h3>
        <p data-id="element-84">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado,
          alteración, divulgación o destrucción. Utilizamos cifrado SSL en todas
          las transmisiones de datos.
        </p>
      </div>

      <div data-id="element-85">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-86">
          4. Cookies
        </h3>
        <p data-id="element-87">
          Nuestro sitio utiliza cookies esenciales para el funcionamiento del
          sitio web y cookies analíticas para mejorar tu experiencia. Puedes
          configurar tu navegador para rechazar cookies, aunque esto puede
          afectar la funcionalidad del sitio.
        </p>
      </div>

      <div data-id="element-88">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-89">
          5. Tus Derechos
        </h3>
        <p data-id="element-90">
          Tienes derecho a acceder, rectificar o eliminar tus datos personales
          en cualquier momento. También puedes solicitar la portabilidad de tus
          datos o retirar tu consentimiento para comunicaciones de marketing.
        </p>
      </div>

      <div data-id="element-91">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-92">
          6. Terceros
        </h3>
        <p data-id="element-93">
          No vendemos, intercambiamos ni transferimos tu información personal a
          terceros, excepto a proveedores de servicios de confianza que nos
          asisten en la operación de nuestro sitio web y en el procesamiento de
          pagos, siempre que dichas partes acuerden mantener esta información
          confidencial.
        </p>
      </div>

      <div data-id="element-94">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-95">
          7. Cambios en esta Política
        </h3>
        <p data-id="element-96">
          Nos reservamos el derecho de actualizar esta política de privacidad en
          cualquier momento. Los cambios serán publicados en esta página con la
          fecha de actualización correspondiente.
        </p>
      </div>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-brand-gray/50 italic" data-id="element-97">
        Última actualización: Marzo 2026
      </p>

      <div data-id="element-98">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-99">
          1. Aceptación de los Términos
        </h3>
        <p data-id="element-100">
          Al acceder y utilizar este sitio web y al realizar una compra, aceptas
          estar sujeto a estos Términos y Condiciones. Si no estás de acuerdo
          con alguna parte de estos términos, no debes utilizar nuestro sitio ni
          adquirir nuestros productos.
        </p>
      </div>

      <div data-id="element-101">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-102">
          2. Naturaleza del Producto
        </h3>
        <p data-id="element-103">
          "El Escudo Hormonal 40+" es un producto digital educativo e
          informativo. Los contenidos proporcionados son de carácter general y
          no constituyen asesoramiento médico, diagnóstico ni tratamiento.
          Siempre consulta con un profesional de la salud calificado antes de
          realizar cambios en tu dieta o estilo de vida.
        </p>
      </div>

      <div data-id="element-104">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-105">
          3. Política de No Reembolso
        </h3>
        <p data-id="element-106">
          Debido a la naturaleza digital e instantánea de este producto, todas
          las ventas son finales y no se ofrecen reembolsos. Al completar tu
          compra, recibirás acceso inmediato a todos los materiales digitales
          incluidos en tu pedido. Una vez entregado el acceso, no es posible
          revertir la transacción.
        </p>
      </div>

      <div data-id="element-107">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-108">
          4. Propiedad Intelectual
        </h3>
        <p data-id="element-109">
          Todo el contenido incluido en este producto — textos, gráficos,
          protocolos, recetas y materiales complementarios — está protegido por
          derechos de autor. Se concede una licencia personal e intransferible
          para uso individual. Queda prohibida la reproducción, distribución o
          reventa de cualquier parte del material.
        </p>
      </div>

      <div data-id="element-110">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-111">
          5. Resultados
        </h3>
        <p data-id="element-112">
          Los testimonios y resultados mostrados en este sitio son experiencias
          individuales y no garantizan que obtendrás los mismos resultados. Los
          resultados pueden variar según factores individuales como el estado de
          salud, la adherencia al programa y otros factores personales.
        </p>
      </div>

      <div data-id="element-113">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-114">
          6. Limitación de Responsabilidad
        </h3>
        <p data-id="element-115">
          En ningún caso seremos responsables de daños directos, indirectos,
          incidentales o consecuentes derivados del uso o la incapacidad de uso
          de nuestros productos digitales. El uso de la información
          proporcionada es bajo tu propia responsabilidad.
        </p>
      </div>

      <div data-id="element-116">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-117">
          7. Soporte Técnico
        </h3>
        <p data-id="element-118">
          Ofrecemos soporte técnico para problemas de acceso a los materiales
          digitales. Nuestro equipo se compromete a responder consultas técnicas
          en un plazo máximo de 24 horas hábiles.
        </p>
      </div>

      <div data-id="element-119">
        <h3 className="font-bold text-brand-green mb-2 text-base" data-id="element-120">
          8. Modificaciones
        </h3>
        <p data-id="element-121">
          Nos reservamos el derecho de modificar estos Términos y Condiciones en
          cualquier momento. Las modificaciones entrarán en vigor inmediatamente
          después de su publicación en el sitio web. El uso continuado del sitio
          después de dichos cambios constituye tu aceptación de los nuevos
          términos.
        </p>
      </div>
    </>;
}